import { HTMLAttributes, forwardRef, useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Button, Icon } from '@factorialco/factorial-one-react'
import { PersonAvatar } from '@factorialco/factorial-one-react/dist/experimental'
import {
  Desktop,
  Maximize,
  MicrophoneNegative,
  Minimize,
} from '@factorialco/factorial-one-react/icons/app'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { isTrackReference } from '@livekit/components-core'
import {
  useAudioWaveform,
  useConnectionQualityIndicator,
  useIsMuted,
  useIsSpeaking,
} from '@livekit/components-react'
import { Track } from 'livekit-client'
import type { Participant } from 'livekit-client'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'

import { SpeakingIndicator } from '../SpeakingIndicator'
import { VideoTrack } from '../VideoTrack'

interface ParticipantTileProps extends HTMLAttributes<HTMLElement> {
  trackRef: TrackReferenceOrPlaceholder
  participant: Participant
  numberOfTracks: number
  onFocus?: () => void
  onUnfocus?: () => void
  isFocused?: boolean
}

export const ParticipantTile = forwardRef<HTMLElement, ParticipantTileProps>(
  function MyParticipantTile(
    { trackRef, participant, numberOfTracks, onFocus, onUnfocus, isFocused },
    ref
  ) {
    const { meeting } = useMeetingDetails()
    const attendees = meeting?.attendees || []

    const [isHovered, setIsHovered] = useState(false)
    const isSpeaking = useIsSpeaking(participant)

    const micTrack = participant?.getTrackPublication(Track.Source.Microphone)
    const micRef: TrackReferenceOrPlaceholder = useMemo(() => {
      return {
        participant: participant,
        source: Track.Source.Microphone,
        publication: micTrack,
      }
    }, [participant, micTrack])

    const isMuted = useIsMuted(micRef)
    const audioWaveform = useAudioWaveform(micRef, {
      barCount: 5,
      volMultiplier: 10,
      updateInterval: 200,
    })

    const isVideoTrack =
      isTrackReference(trackRef) &&
      (trackRef.publication?.kind === 'video' ||
        trackRef.source === Track.Source.Camera ||
        trackRef.source === Track.Source.ScreenShare)

    const participantId = participant?.identity || 'Local'
    const isScreenShare = trackRef?.source === Track.Source.ScreenShare

    const attendee = attendees.find(
      ({ employee }) => Number(employee?.id) === Number(participantId)
    )

    const participantFirstName =
      attendee?.employee?.firstName || i18n.t('meetings.unknown_participant')
    const participantLastName = attendee?.employee?.lastName || ''
    const participantName = attendee?.employee?.fullName || i18n.t('meetings.unknown_participant')
    const participantAvatar = buildUrl(attendee?.employee?.avatar?.url) || undefined
    const { quality } = useConnectionQualityIndicator({ participant })

    const isLocalCamera =
      isTrackReference(trackRef) &&
      trackRef.participant.isLocal &&
      trackRef.source === Track.Source.Camera

    const facingMode = isLocalCamera ? 'user' : undefined

    const isTrackVideoEnabled =
      (isVideoTrack && trackRef?.participant?.isCameraEnabled && isTrackReference(trackRef)) ||
      isScreenShare

    return (
      <Box
        style={{
          boxShadow: isSpeaking && !isScreenShare ? '0px 0px 18px 0px #07A2AD99' : undefined,
          width: '100%',
        }}
        height="full"
        overflowY="hidden"
        overflowX="hidden"
        ref={ref}
        background="grey1000"
        position="relative"
        alignItems="center"
        justifyContent="center"
        border={{
          all: {
            width: 's1',
            style: isTrackVideoEnabled ? 'hidden' : 'solid',
            color: 'grey800',
          },
        }}
        borderRadius={{ all: 'abs016' }}
      >
        {isTrackVideoEnabled ? (
          <VideoTrack trackRef={trackRef} facingMode={facingMode} isScreenShare={isScreenShare} />
        ) : (
          <PersonAvatar
            firstName={participantFirstName}
            lastName={participantLastName}
            size="xlarge"
            src={participantAvatar}
          />
        )}

        <Box
          position="absolute"
          style={{
            top: '8px',
            left: '8px',
            zIndex: '50',
          }}
          background="grey1000"
          borderRadius={{ all: 'abs008' }}
          paddingY="s4"
          paddingX="s8"
          flexDirection="row"
        >
          <Text color="grey000White" size="100" weight="semibold" title={participantName}>
            {participantFirstName}
          </Text>
          <Box
            style={{
              transition: 'all 0.2s ease-in-out',
              width: isSpeaking || (isMuted && !isScreenShare) || isScreenShare ? 'auto' : '0',
              paddingLeft: isSpeaking || (isMuted && !isScreenShare) || isScreenShare ? '8px' : '0',
            }}
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            {isMuted && !isScreenShare && (
              <Icon icon={MicrophoneNegative} size="sm" className="text-f1-foreground-critical" />
            )}
            {isScreenShare && <Icon icon={Desktop} size="sm" className="text-f1-foreground-info" />}
            {isSpeaking && !isScreenShare && <SpeakingIndicator bars={audioWaveform.bars} />}
          </Box>
        </Box>

        <Box
          style={{
            opacity: isHovered ? '1' : '0',
            inset: '0',
            transition: 'opacity 0.2s ease-in-out',
          }}
          position="absolute"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {numberOfTracks > 1 && trackRef && (
            <Box
              position="absolute"
              borderRadius={{ all: 'abs008' }}
              style={{
                top: '8px',
                right: '8px',
              }}
              background="grey000White"
            >
              <Button
                size="sm"
                hideLabel
                variant="secondary"
                onClick={isFocused ? onUnfocus : onFocus}
                icon={isFocused ? Minimize : Maximize}
              />
            </Box>
          )}

          {quality && (
            <Box
              gap="s2"
              position="absolute"
              style={{
                bottom: '8px',
                right: '8px',
              }}
              flexDirection="row"
              alignItems="flexEnd"
              justifyContent="center"
              padding="s2"
            >
              <Box
                borderRadius={{ all: 'abs004' }}
                background="grey000White"
                style={{
                  width: '4px',
                  height: '4px',
                  opacity:
                    quality === 'excellent' || quality === 'good' || quality === 'poor'
                      ? '1'
                      : '0.3',
                }}
              />
              <Box
                borderRadius={{ all: 'abs004' }}
                background="grey000White"
                style={{
                  width: '4px',
                  height: '8px',
                  opacity: quality === 'excellent' || quality === 'good' ? '1' : '0.3',
                }}
              />
              <Box
                borderRadius={{ all: 'abs004' }}
                background="grey000White"
                style={{
                  width: '4px',
                  height: '12px',
                  opacity: quality === 'excellent' ? '1' : '0.3',
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    )
  }
)
